import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl} from '@angular/forms';
import {manufactureDateRangeValidator} from '../../sensor/services/validator/manufacture-date-range.validator';
import {debounce, finalize, from, mergeMap, Observable, of, Subject, switchMap, takeUntil, tap, timer} from 'rxjs';
import {PatientRestApiService} from '../services/http/patient-rest-api.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ApiPatientListResponse} from '../services/http/api-patient-list-response';
import {ApiPatientsListPaginatedQueryParams} from '../services/http/api-patients-list-paginated-query-params';
import {MatSort, SortDirection} from '@angular/material/sort';
import {Utils} from '@astek/utils';
import {ApiPatientsListItemResponse} from '../services/http/api-patients-list-item-response';
import {PageEvent} from '@angular/material/paginator';
import {ApiRefreshTimerEnum} from '../../../core/services/http/api-refresh-timer.enum';
import {RoutesEnum} from '../../../core/services/navigation/routes.enum';
import {PatientsRoutesEnum} from '../services/navigation/patients-routes.enum';
import {Router} from '@angular/router';

@Component({
  selector: 'xp-patients-list',
  templateUrl: './patients-list.component.html',
  styleUrls: ['../../crud.common.scss', './patients-list.component.scss'],
})
export class PatientsListComponent implements OnInit, AfterViewInit {

  public isLoading: boolean = true;

  public patients: Array<ApiPatientsListItemResponse> | null = null;

  public itemsCount: number = 0;
  public pageIndex: number = 0;
  public pageSize: number = 10;

  public displayedColumns: Array<string> = [
    'lastName',
    'firstName',
    'medicalId',
    'createdAt',
    'actions',
  ];

  public readonly formGroup = this._formBuilder.group({
    search: new FormControl<string>(''),
    createdAtFrom: new FormControl<Date | null>(null),
    createdAtTo: new FormControl<Date | null>(null),
  }, {validators: [manufactureDateRangeValidator]});

  public defaultSortOn: string = 'lastName';
  public defaultSortOrder: SortDirection = 'asc';
  @ViewChild(MatSort)
  public sort: MatSort = new MatSort();

  private _requestApiOnChangeSubject: Subject<number> = new Subject<number>();

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _patientRestApiService: PatientRestApiService,
    private readonly _router: Router,
  ) {
    this._requestApiOnChangeSubject.pipe(
      takeUntilDestroyed(),
      debounce((dueTime: number) => timer(dueTime)),
      switchMap(() => this.requestPatients().pipe(
        takeUntil(this._requestApiOnChangeSubject),
      )),
    ).subscribe({
      next: (response: ApiPatientListResponse | null) => {
        if (Utils.isNotNullOrUndefined(response)) {
          this.handleRequestPatientsResponse(response);
        }
      },
    });
  }

  public get searchValue(): string {
    return this.formGroup.controls.search.value?.trim() ?? '';
  }

  public ngOnInit(): void {
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.resetFilters();
    }, 200);
  }

  public goToDetailsPage(id: string): void {
    this.isLoading = true;
    from(this._router.navigate([
      '/',
      RoutesEnum.PATIENTS,
      PatientsRoutesEnum.DETAILS,
      id,
    ])).subscribe({
      next: () => {
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Unable to navigate to patient details page:', error);
        this.isLoading = false;
      },
    });
  }

  public clearSearch(): void {
    this.pageIndex = 0;
    this.formGroup.controls.search.setValue('');
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public onSearchInput(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.KEYBOARD_INPUT);
  }

  public onDateChange(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onFilterChange(): void {
    this.pageIndex = 0;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onSortChange(): void {
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.MOUSE_CLICK);
  }

  public onPaginatorChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this._requestApiOnChangeSubject.next(ApiRefreshTimerEnum.INSTANTLY);
  }

  public resetFilters(): void {
    this.formGroup.controls.search.setValue('');
    this.formGroup.controls.createdAtFrom.setValue(null);
    this.formGroup.controls.createdAtTo.setValue(null);
    this.clearSearch();
    this.formGroup.markAsPristine();
  }

  public openClinicalReport(patient: ApiPatientsListItemResponse): void {
    this.isLoading = true;

    this._patientRestApiService.requestReport(patient.id).pipe(
      mergeMap((blob: Blob): Observable<string> => {
        return of(window.URL.createObjectURL(blob));
      }),
      mergeMap((fileUri: string): Observable<void> => {
        return of(void 0).pipe(
          tap(() => window.open(fileUri)),
        );
      }),
      finalize(() => this.isLoading = false),
    ).subscribe({});
  }

  private requestPatients(): Observable<ApiPatientListResponse> {
    this.isLoading = true;
    const queryParams: ApiPatientsListPaginatedQueryParams = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sort: this.sort?.active ?? this.defaultSortOn,
      order: this.sort?.direction ?? this.defaultSortOrder,
      search: this.searchValue,
      createdAtFrom: this.formGroup.controls.createdAtFrom.value,
      createdAtTo: this.formGroup.controls.createdAtTo.value,
    };
    return this._patientRestApiService.requestPatientListPaginated(queryParams);
  }

  private handleRequestPatientsResponse(response: ApiPatientListResponse): void {
    this.isLoading = false;
    this.itemsCount = response.totalCount;
    this.patients = response.patients;
  }
}
